import {Button, Flex, Form, Input, Select, Skeleton, Alert, Tooltip, Checkbox, Modal, message} from "antd";
import {
  WorkOrderForm, WorkOrderFormModel,
  workOrderOptions,
  workOrderPriorityOptions, WorkOrderStatus, WorkOrderType, WorkOrderUpdateFormModel
} from "../../../../../../store/data/entities/work-order";
import { PlusOutlined } from "@ant-design/icons";
import {useGetUsersOfRoleQuery} from "../../../../../../store/services/api/user-api";
import React, {useEffect, useState} from "react";
import AssetFormControl from "../../../../../../components/form-controls/AssetFormControl";
import {
  useCreateWorkOrderMutation,
  useLazyGetWorkOrderQuery,
  useUpdateWorkOrderMutation,
  useGetWorkOrderCalculatedFieldsMutation
} from "../../../../../../store/services/api/work-order-api";
import { useCreateExternalUserMutation } from "../../../../../../store/services/api/user-api";

import WorkOrderApprovalModal from "./WorkOrderApprovalModal";
import {useFormFieldsDisable} from "../../../../../../hooks/useFormFieldsDisable";
import {humanizeDate} from "../../../../../../utils/humanizers";
import {InfoCircleFilled, LoadingOutlined} from "@ant-design/icons";
import {SelectOption} from "../../../../../../store/data/models/select-option";
import useUser from "../../../../../../hooks/useUser";
import LuxonDatePicker from "../../../../../../components/form-controls/LuxonDatePicker";
import {DateTime} from "luxon";
import type {UserRole} from "../../../../../../store/data/users/user-roles";
import { useGetAllFallOutsQuery } from "../../../../../../store/services/api/fall-out-api";
import useSubmitOnEnter from "../../../../../../hooks/useSubmitOnEnter";

interface WorkOrderManageFormProps {
  onSuccess?: () => void;
  workOrderId?: string | undefined;
  ticketId?: string | undefined;
  isApproval?: boolean;
  assetOption?: SelectOption | undefined;
}

export default function WorkOrderManageForm(props: WorkOrderManageFormProps) {
  const scheduledStartDateEditorRoles: readonly UserRole[] = ['Admin'];
  const scheduledFinishDateEditorRoles: readonly UserRole[] = ['Admin', 'Helpdesk'];
  const [createWorkOrder, {isLoading: createLoading}] = useCreateWorkOrderMutation();
  const [updateWorkOrder, {isLoading: updateLoading}] = useUpdateWorkOrderMutation();
  const [getCalculatedFields, {isLoading: fieldsLoading, data: calculatedFields}] = useGetWorkOrderCalculatedFieldsMutation();

  const [getWorkOrder, {data: initialFormData, isFetching: initialFormDataLoading}] = useLazyGetWorkOrderQuery();

  const {data: technicians, isFetching: techniciansFetching} = useGetUsersOfRoleQuery(['Technician']);
  const {data: buildingLeads, isFetching: buildingLeadsFetching} = useGetUsersOfRoleQuery(['BuildingLead']);
  const {data: fallOuts, isFetching: fallOutsFetching} = useGetAllFallOutsQuery();
   const {
  data: externalTechnicians,
  isFetching: externalTechniciansFetching,
  refetch: refetchExternalTechnicians
} = useGetUsersOfRoleQuery(['ExternalTechnician']);
  const optionsLoading = techniciansFetching || buildingLeadsFetching || externalTechniciansFetching || fallOutsFetching;

  const {user} = useUser();
  const canEditScheduledStartDate = scheduledStartDateEditorRoles.includes(user.role);
  const canEditScheduledFinishDate = scheduledFinishDateEditorRoles.includes(user.role);

  const isPostApprovalEdit = initialFormData &&
    !(initialFormData?.status === WorkOrderStatus.PendingApproval || initialFormData?.status === WorkOrderStatus.Rejected);

  const hidePriorityScore = initialFormData?.type === WorkOrderType.Preventative || props.isApproval;

  

  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [form] = Form.useForm<WorkOrderForm>();
  const {isDisabled, setFieldDisabled, setFieldsDisabled} = useFormFieldsDisable<WorkOrderForm>({
    id: true,
    reference: true,
    description: props.isApproval,
    type: props.isApproval,
    technicianId: props.isApproval,
    isExternal: props.isApproval,
    externalTechnicianName: props.isApproval,
    externalTechnicianEmail: props.isApproval,
    buildingLeadId: props.isApproval,
    fallOutId: props.isApproval,
    priority: props.isApproval,
    scheduledStartDate: props.isApproval || !canEditScheduledStartDate,
    scheduledFinishDate: props.isApproval || !canEditScheduledFinishDate,
    assets: !!props.ticketId || props.isApproval
  });
  const startDate = Form.useWatch('scheduledStartDate', form);
  const assets = Form.useWatch('assets', form);
  const priority = Form.useWatch('priority', form);
  const isExternal = Form.useWatch("isExternal", form);
  const handleEnterSubmit = useSubmitOnEnter(form, !props.isApproval);

  
  const [createExternalUser, { isLoading: creatingExternal }] = useCreateExternalUserMutation();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newExternalForm] = Form.useForm();


  const handleAddExternalTechnician = async () => {
    try {
      const values = await newExternalForm.validateFields();

      const newUserPayload = {
        name: values.name,
        email: values.email,
        role: "ExternalTechnician",
        isExternal: true,
      };

      await createExternalUser(newUserPayload).unwrap();

      const refreshed = await refetchExternalTechnicians().unwrap();

      const updatedOptions = mapExternalUserOptions(refreshed);

      const newlyAddedTech = refreshed.find(t => t.email === values.email);
      if (newlyAddedTech) {
          form.setFieldValue("technicianId", newlyAddedTech.id);
      }

      message.success("External technician added and selected successfully.");
      newExternalForm.resetFields();
      setIsAddModalOpen(false);
    } catch (error: any) {
      message.error(error?.data?.message || "Failed to add external technician.");
    }
  };

  const handleOnFinish = (data: any) => {
    console.log(data);
    const formData: WorkOrderFormModel = convertFormData(data);

    if (props.workOrderId) {
      updateWorkOrder(formData as WorkOrderUpdateFormModel)
        .then(() => {
          props.onSuccess?.();
        })
      return;
    }

    createWorkOrder(formData)
      .then(() => {
        props.onSuccess?.();
      })
  }

  const mapFallOutOptions = (fallOuts: any) =>
    (fallOuts ?? []).map(item => ({
      label: item.fallOutCode,
      value: item.id
    }));

  const mapUserOptions = (usersList: any) =>
    (usersList ?? []).map(item => ({
      label: item.name,
      value: item.id
    }));

    const mapExternalUserOptions = (usersList: any) =>
    (usersList ?? []).map(item => ({
      label: item.name + ": " + item.email,
      value: item.id
    }));
  const convertFormData = (data: WorkOrderForm): WorkOrderFormModel => ({
    ...data,
    assets: data.assets?.map(item => item.value) ?? [],
    scheduledFinishDate: data.scheduledFinishDate.toUTC().toISO(),
    scheduledStartDate: data.scheduledStartDate.toUTC().toISO()
  });

  useEffect(() => {
    if (!props.workOrderId) {
      form.resetFields();
      return;
    }
    getWorkOrder({
      id: props.workOrderId
    });
  }, [props.workOrderId]);

  useEffect(() => {
    if (!initialFormData) return;
    console.log(initialFormData);

    form.setFieldValue('id', initialFormData.id);
    form.setFieldValue('reference', initialFormData.reference);
    form.setFieldValue('description', initialFormData.description);
    form.setFieldValue('type', initialFormData.type);
    form.setFieldValue('technicianId', initialFormData.technicianId);
    form.setFieldValue('buildingLeadId', initialFormData.buildingLeadId);
    form.setFieldValue('fallOutId', initialFormData.fallOutId);
    form.setFieldValue('priority', initialFormData.priority);
    form.setFieldValue('scheduledStartDate', DateTime.fromISO(initialFormData.scheduledStartDate));
    form.setFieldValue('scheduledFinishDate', DateTime.fromISO(initialFormData.scheduledFinishDate));
    form.setFieldValue('assets', initialFormData.assignedAssetOptions);
    form.setFieldValue('isExternal', initialFormData.isExternal);
    form.setFieldValue('externalTechnicianName', initialFormData.externalTechnicianName);
    form.setFieldValue('externalTechnicianEmail', initialFormData.externalTechnicianEmail);

    setFieldDisabled('type', initialFormData.type === WorkOrderType.Preventative);

    if (isPostApprovalEdit && user.role !== 'Admin') {
      setFieldsDisabled({
        description: true,
        type: true,
        assets: true
      })
    }
  }, [initialFormData, setFieldsDisabled]);

  useEffect(() => {
    if (!props.ticketId) return;
    form.setFieldValue('ticketId', props.ticketId);
    form.setFieldValue('assets', [props.assetOption]);

  }, [props.ticketId, props.assetOption]);

  useEffect(() => {
    if (hidePriorityScore || priority == undefined) return;

    getCalculatedFields({
      scheduledStartDate: startDate?.toUTC().toISO(),
      priority: priority,
      assets: assets?.map(item => item.value) ?? [],
      timezoneId: DateTime.local().zoneName
    })
      .unwrap()
      .then(res => {
        if (isPostApprovalEdit) return;
        const finishDate: DateTime = DateTime.fromISO(res.scheduledFinishDate);
        if (finishDate.isValid) {
          form.setFieldValue('scheduledFinishDate', finishDate);
        }
      })
  }, [startDate, assets, priority]);

  const handleOnApproval = () => {
    setShowApprovalModal(false);
    props.onSuccess?.();
  }

  const PriorityScoreAlert = () => {
    const description = calculatedFields
      ? `Based on the given assets and work order priority, the work order must be completed by ${humanizeDate(calculatedFields.scheduledFinishDate)}`
      : "Please select a Priority and assets to view the priority score."

    const message = calculatedFields
      ? `Priority Score: ${calculatedFields.priorityScore}`
      : "Priority Score"

    return (
      <Alert
        icon={fieldsLoading ? <LoadingOutlined /> : <InfoCircleFilled />}
        message={message}
        showIcon
        description={description}
        type="info"
      />
    )
  }

  const formDisabled = createLoading || updateLoading || props.isApproval;

  return (
    <Skeleton loading={optionsLoading || initialFormDataLoading} active>
  <Form<WorkOrderForm>
    onFinish={handleOnFinish}
    form={form}
    layout="vertical"
    disabled={createLoading || updateLoading}
    onKeyDown={handleEnterSubmit}
  >
    <Form.Item name="id" hidden><Input /></Form.Item>
    <Form.Item name="ticketId" hidden><Input /></Form.Item>


    <Flex gap={16} wrap align="start">
      <Form.Item name="reference" label="Reference" style={{ flex: 1 }}>
        <Input disabled />
      </Form.Item>

      <Form.Item
        name="description"
        label="Description"
        rules={[{ required: true }]}
        style={{ flex: 1 }}
      >
        <Input disabled={isDisabled["description"]} />
      </Form.Item>
      <Form.Item
        name="fallOutId"
        label="Fault Code"
        rules={[{ required: false }]}
        style={{ flex: 1 }}
      >
        <Select
          showSearch
          allowClear
          optionFilterProp="label"
          filterOption={(input, option) =>
            String(option?.label ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          disabled={isDisabled["fallOutId"]}
          options={mapFallOutOptions(fallOuts)}
        />
      </Form.Item>
    </Flex>
    <Flex gap={16} wrap align="start">
      <Form.Item
        name="type"
        label="Type"
        rules={[{ required: true }]}
        style={{ flex: 1 }}
      >
        <Select
          disabled={isDisabled["type"] || formDisabled}
          options={workOrderOptions}
        />
      </Form.Item>

      <Form.Item
        name="priority"
        label="Priority"
        rules={[{ required: true }]}
        style={{ flex: 1 }}
      >
        <Select
          disabled={isDisabled["priority"]}
          options={workOrderPriorityOptions}
        />
      </Form.Item>
      
    </Flex>

    <Flex gap={16} align="start" wrap>
  <div style={{ flex: 1, minWidth: 300 }}> 
    <Flex gap={16} align="end" style={{ width: "100%" }}>
  <Form.Item
  name="isExternal"
  label="External Technician"
  valuePropName="checked"
>
  <Checkbox
    onChange={(e) => {
      form.setFieldsValue({ technicianId: null });
      form.validateFields(["technicianId"]).catch(() => {});
    }}
    disabled={isDisabled["isExternal"]}
  >
    Is External
  </Checkbox>
</Form.Item>

<Form.Item
  shouldUpdate={(prevValues, currValues) => prevValues.isExternal !== currValues.isExternal}
  noStyle
>
  {({ getFieldValue }) => {
    const isExternalValue = getFieldValue("isExternal");
    const options = isExternalValue
      ? mapExternalUserOptions(externalTechnicians)
      : mapUserOptions(technicians);

    const selectKey = `tech-select-${isExternalValue ? "external" : "internal"}`;

    return (
      <Form.Item
        label={isExternalValue ? "External Technician" : "Technician"}
        name="technicianId"
        rules={[{ required: true, message: "Please select a technician" }]}
        style={{ flex: 1 }}
      >
        {/* Separate Form.Item here ensures live re-render */}
        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.technicianId !== curr.technicianId}>
          {({ getFieldValue }) => (
            <Flex gap={8}>
              <Select
                key={selectKey}
                showSearch
                allowClear
                placeholder={
                  isExternalValue
                    ? "Select external technician"
                    : "Select technician"
                }
                options={options}
                style={{ flex: 1 }}
                disabled={isDisabled["technicianId"]}
                value={getFieldValue("technicianId")} // controlled
                onChange={(val) => {
                  form.setFieldValue("technicianId", val);
                  form.validateFields(["technicianId"]).catch(() => {});
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
              {isExternalValue && (
                <Button
                  icon={<PlusOutlined />}
                  type="dashed"
                  disabled={isDisabled["technicianId"]}
                  onClick={() => setIsAddModalOpen(true)}
                >
                  Add New
                </Button>
              )}
            </Flex>
          )}
        </Form.Item>
      </Form.Item>
    );
  }}
</Form.Item>
  

 
</Flex>

    
  </div>


  <div style={{ flex: 1, minWidth: 300 }}>
    <Form.Item
      name="buildingLeadId"
      label="Owner"
      rules={[{ required: true }]}
    >
      <Select
        disabled={isDisabled["buildingLeadId"]}
        options={mapUserOptions(buildingLeads)}
      />
    </Form.Item>
  </div>
</Flex>


    <Flex align='end' gap={16} wrap>
       <div style={{flex: 1, flexBasis: 240}}>
         <Tooltip title={canEditScheduledStartDate ? null : "You don't have permission to edit this date."}>
           <Form.Item 
           name='scheduledStartDate' 
           label='Created On' 
           rules={[{ required: true }]} 
           initialValue={DateTime.now().startOf('day')} >
             <LuxonDatePicker showNow={false} style={{ width: '100%' }} disabled={isDisabled['scheduledStartDate']} />
            </Form.Item> </Tooltip> <Tooltip title={canEditScheduledFinishDate ? null : "You don't have permission to edit this date."}>
          
           <Form.Item name='scheduledFinishDate' label='Must Finish By' rules={[{ required: true }]} >
             <LuxonDatePicker showNow={false} style={{ width: '100%' }} disabled={isDisabled['scheduledFinishDate'] || fieldsLoading} />
           </Form.Item> 
          </Tooltip>
       </div>
       <div style={{flex: 1, flexBasis: 240, paddingBottom: 24}}>
         {!hidePriorityScore && ( <PriorityScoreAlert /> )} 
      </div> 
    </Flex>

    <Form.Item name="assets" label="Assets">
      <AssetFormControl
        disabled={isDisabled["assets"]}
        multiple
        menuPlacement="topLeft"
        enableEdit
      />
    </Form.Item>

    

    {!props.isApproval ? (
      <Button type="primary" htmlType="submit">
        Save
      </Button>
    ) : (
      <Button type="primary" onClick={() => setShowApprovalModal(true)}>
        Approve / Reject
      </Button>
    )}
  </Form>

  <WorkOrderApprovalModal
    id={props.workOrderId}
    onClose={() => setShowApprovalModal(false)}
    onSuccess={handleOnApproval}
    open={showApprovalModal}
  />

  <Modal
    title="Add External Technician"
    open={isAddModalOpen}
    onCancel={() => setIsAddModalOpen(false)}
    onOk={handleAddExternalTechnician}
    okText="Add Technician"
  >
    <Form form={newExternalForm} layout="vertical">
      <Form.Item
        label="Name"
        name="name"
        rules={[{ required: true, message: "Please enter technician name" }]}
      >
        <Input placeholder="Technician Name" />
      </Form.Item>
      <Form.Item
        label="Email"
        name="email"
        rules={[
          { required: true, message: "Please enter technician email" },
          { type: "email", message: "Invalid email format" },
        ]}
      >
        <Input placeholder="Technician Email" />
      </Form.Item>
    </Form>
  </Modal>
</Skeleton>

  )
}
